import { useState, useCallback, type ReactNode, type FormEvent } from "react";
import { API_BASE } from "./appUtils";
import { readResetLink } from "./resetLink";
import { landingPage } from "./landingPageContent";

const STORAGE_KEY = "cw_access_granted";
const DEMO_KEY = "cw_demo_visit";

// The team access code is checked by the server (/auth/team-access), so it
// is never part of the public JavaScript bundle. It only opens the team
// sign-in screen; signing in still needs a team account.
async function checkTeamCode(code: string): Promise<"ok" | "wrong" | "busy" | "offline"> {
  try {
    const res = await fetch(`${API_BASE}/auth/team-access`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });
    if (res.ok) return "ok";
    if (res.status === 429) return "busy";
    return "wrong";
  } catch {
    return "offline";
  }
}

function hasAccess(): boolean {
  try {
    // A password reset link from the email opens the team sign-in side
    // directly. The gate is not a security boundary: resetting still needs
    // a valid code for that email, checked by the server.
    if (readResetLink()) {
      sessionStorage.setItem(STORAGE_KEY, "1");
      sessionStorage.removeItem(DEMO_KEY);
      return true;
    }
    return sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return !!readResetLink();
  }
}

/** Leave the public demo: clear the demo session and return to the notice. */
export function exitDemo(): void {
  try {
    sessionStorage.removeItem(DEMO_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* sessionStorage may be unavailable */ }
  window.location.replace("/");
}

/** True when the visitor entered through the public demo notice (no account). */
export function isDemoVisit(): boolean {
  try {
    return sessionStorage.getItem(DEMO_KEY) === "1";
  } catch {
    return false;
  }
}

export default function AccessGate({ children }: { children: ReactNode }) {
  const [unlocked, setUnlocked] = useState(hasAccess);
  const [understood, setUnderstood] = useState(false);
  const [showTeam, setShowTeam] = useState(false);
  const [code, setCode] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [error, setError] = useState<false | "wrong" | "busy" | "offline">(false);
  const [checking, setChecking] = useState(false);

  const enterDemo = useCallback(() => {
    if (!understood) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
      sessionStorage.setItem(DEMO_KEY, "1");
    } catch { /* sessionStorage may be unavailable */ }
    setUnlocked(true);
  }, [understood]);

  const handleTeamSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!code || checking) return;
      setChecking(true);
      const result = await checkTeamCode(code);
      setChecking(false);
      if (result === "ok") {
        try {
          sessionStorage.setItem(STORAGE_KEY, "1");
          sessionStorage.removeItem(DEMO_KEY);
        } catch { /* sessionStorage may be unavailable */ }
        setUnlocked(true);
      } else {
        setError(result);
        if (result === "wrong") setCode("");
      }
    },
    [code, checking],
  );

  if (unlocked) return <>{children}</>;

  const bodyText = {
    color: "var(--color-text-secondary, #d1d0d8)",
    fontSize: "var(--fs-sm)",
    lineHeight: 1.55,
    margin: "0 0 0.9rem",
    textAlign: "left" as const,
  };

  return (
    <main
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--color-bg-primary, #0c0a14)",
        fontFamily: "'Atkinson Hyperlegible', sans-serif",
        padding: "1rem",
      }}
    >
      <div
        style={{
          background: "var(--color-bg-card, #262240)",
          borderRadius: "var(--radius-lg)",
          padding: "2.25rem 1.75rem",
          maxWidth: "440px",
          width: "100%",
          textAlign: "center",
          boxShadow: 'var(--shadow-overlay)',
        }}
      >
        <img src="/careweave-net-icon.svg" alt="" width="56" height="56" style={{ display: "inline-block", marginBottom: 12 }} />
        <h1
          style={{
            fontSize: "var(--fs-xl)",
            fontWeight: 800,
            fontFamily: "var(--font-display, 'Source Serif 4', Georgia, serif)",
            color: "var(--color-accent)",
            margin: "0 0 1rem",
          }}
        >
          CareWeave
        </h1>

        <p style={bodyText}>
          CareWeave is a research prototype that is being co-designed with young people who live with sickle cell
          disease. This demo is for reviewers and research partners.
        </p>
        <p style={bodyText}>
          The demo uses fictional profiles and example data. CareWeave is not a health service and does not give
          medical advice. You cannot create an account or contact other people through this site. Please do not
          enter real personal or health information.
        </p>
        <p style={bodyText}>
          If you need health support, please contact your care team. In an emergency, call 911.
        </p>

        <label
          style={{
            display: "flex",
            gap: "0.6rem",
            alignItems: "flex-start",
            textAlign: "left",
            color: "var(--color-text-primary, #fff)",
            fontSize: "var(--fs-sm)",
            lineHeight: 1.5,
            margin: "0.4rem 0 1rem",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={understood}
            onChange={(e) => setUnderstood(e.target.checked)}
            style={{ width: 20, height: 20, marginTop: 2, flexShrink: 0, accentColor: "var(--color-accent, var(--color-accent))" }}
          />
          <span>I understand that this is a demo with fictional data. It is not a service I can join or use for care.</span>
        </label>

        <button
          type="button"
          className="btn btn-primary"
          onClick={enterDemo}
          disabled={!understood}
          aria-disabled={!understood}
        >
          <span>Enter the demo</span>
        </button>

        {!showTeam && (
          /* eslint-disable-next-line cw/no-inline-button-style -- AccessGate text link for research team sign-in */
          <button
            type="button"
            onClick={() => setShowTeam(true)}
            style={{
              marginTop: "1.1rem",
              background: "none",
              border: "none",
              color: "var(--color-text-secondary, #d1d0d8)",
              fontSize: "var(--fs-xs)",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            Research team access
          </button>
        )}

        {showTeam && (
          <form onSubmit={handleTeamSubmit} style={{ marginTop: "1.25rem", display: "grid", gap: "0.75rem" }}>
            <div style={{ position: "relative", width: "100%" }}>
              <input
                type={showCode ? "text" : "password"}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  setError(false);
                }}
                placeholder="Team access code"
                aria-label="Team access code"
                autoFocus
                style={{
                  width: "100%",
                  padding: "0.75rem 2.5rem 0.75rem 1rem",
                  borderRadius: "var(--radius-sm)",
                  border: error ? "2px solid var(--color-danger, var(--color-danger))" : "2px solid transparent",
                  background: "var(--color-bg-surface, #1e1a30)",
                  color: "var(--color-text-primary, #fff)",
                  fontSize: "var(--fs-base)",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {/* eslint-disable-next-line cw/no-inline-button-style -- AccessGate password-eye toggle — absolute-positioned icon-only input adornment, not a chamfered .btn variant */}
              <button
                type="button"
                onClick={() => setShowCode((prev) => !prev)}
                aria-label={showCode ? "Hide code" : "Show code"}
                style={{
                  position: "absolute",
                  right: "0.5rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "0.25rem",
                  color: "var(--color-text-secondary, #d1d0d8)",
                  fontSize: "var(--fs-md)",
                  lineHeight: 1,
                }}
              >
                {showCode ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {error && (
              <p style={{ color: "var(--color-danger, var(--color-danger))", fontSize: "var(--fs-sm)", margin: "0.5rem 0 0" }}>
                {error === "busy"
                  ? "Too many tries. Please wait a few minutes and try again."
                  : error === "offline"
                    ? "Couldn't reach CareWeave. Check your connection and try again."
                    : "That code didn't match. Try again."}
              </p>
            )}
            <button type="submit" className="btn btn-secondary" disabled={checking}>
              <span>{checking ? "Checking…" : "Continue to team sign-in"}</span>
            </button>
          </form>
        )}
        <section aria-label={landingPage.landAcknowledgmentTitle} style={{ marginTop: "1.75rem", paddingTop: "1.25rem", borderTop: "1px solid var(--color-accent-light)", textAlign: "left" }}>
          <img
            src="/land-acknowledgment-art.jpeg"
            alt="Indigenous circular artwork by Emily Kewageshig"
            width="96"
            height="96"
            style={{ display: "block", margin: "0 auto 0.75rem", borderRadius: "50%", objectFit: "contain" }}
          />
          <p style={{ fontSize: "var(--fs-xs)", color: "var(--color-text-secondary)", margin: "0 0 0.4rem", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, textAlign: "center" }}>
            {landingPage.landAcknowledgmentTitle}
          </p>
          <p style={{ fontSize: "var(--fs-xs)", color: "var(--color-text-secondary)", margin: "0 0 0.4rem", lineHeight: 1.6 }}>
            {landingPage.landAcknowledgmentBody}
          </p>
          <p style={{ fontSize: "var(--fs-xs)", color: "var(--color-text-secondary)", margin: 0, fontStyle: "italic", textAlign: "center" }}>
            {landingPage.landAcknowledgmentArtCredit}
          </p>

          <p style={{ fontSize: "var(--fs-xs)", color: "var(--color-text-secondary)", margin: "1.25rem 0 0.6rem", letterSpacing: "0.05em", textTransform: "uppercase", textAlign: "center" }}>
            {landingPage.partnerSectionHeading}
          </p>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
            <span style={{ background: "var(--color-text-primary)", borderRadius: "var(--radius-sm)", padding: "5px 12px", opacity: 0.85 }}>
              <img src="/logo-sickkids.png" alt="SickKids, The Hospital for Sick Children" style={{ height: 20, width: "auto", display: "block" }} />
            </span>
            <span style={{ background: "var(--color-text-primary)", borderRadius: "var(--radius-sm)", padding: "5px 12px", opacity: 0.85 }}>
              <img src="/logo-cdt.png" alt="Centre for Digital Therapeutics" style={{ height: 20, width: "auto", display: "block" }} />
            </span>
            <img src="/logo-uoft-correct.jpeg" alt="University of Toronto" style={{ height: 36, width: "auto", borderRadius: "var(--radius-sm)", opacity: 0.9 }} />
          </div>
        </section>
      </div>
    </main>
  );
}
